import { useEffect, useState } from "react";
import { Plus, Edit, Trash2, Shield } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Input, Select, Btn, Toggle, Modal } from "@/components/admin/AdminUI";

const ROLES = [
  { value: "super_admin", label: "Super Admin", desc: "Acceso total, incluida la gestión de usuarios." },
  { value: "editor", label: "Editor", desc: "Gestiona todo el contenido (no usuarios)." },
  { value: "author", label: "Autor", desc: "Solo crea y edita sus propios artículos." },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.value, r.label]));
const ROLE_COLORS = { super_admin: "bg-sunset-accent text-white", editor: "bg-blue-100 text-blue-800", author: "bg-green-100 text-green-800" };
const EMPTY = { email: "", password: "", name: "", role: "author" };

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data || [])).catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ email: u.email, password: "", name: u.name, role: u.role, active: u.active !== false }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || (!editing && (!form.email || !form.password))) { toast.error("Completa los campos requeridos"); return; }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editing.id}`, payload);
      } else {
        await api.post("/admin/users", { email: form.email, password: form.password, name: form.name, role: form.role });
      }
      toast.success("Usuario guardado"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("Usuario eliminado"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Equipo & Roles</h1>
          <p className="text-sunset-muted text-sm mt-1">Gestiona los usuarios y sus permisos de acceso.</p>
        </div>
        <Btn variant="accent" onClick={openNew} data-testid="new-user-btn"><Plus size={15} /> Nuevo usuario</Btn>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {ROLES.map((r) => (
          <div key={r.value} className="bg-white border border-sunset-border p-5">
            <div className="flex items-center gap-2 mb-2"><Shield size={16} className="text-sunset-accent" /><span className="font-semibold">{r.label}</span></div>
            <p className="text-xs text-sunset-muted">{r.desc}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-sunset-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-sunset-border text-left text-xs uppercase tracking-wider text-sunset-muted">
              <th className="px-5 py-3">Nombre</th><th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Rol</th><th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-sunset-border/60 hover:bg-sunset-bg" data-testid={`user-row-${u.id}`}>
                <td className="px-5 py-4 font-medium">{u.name} {u.id === me?.id && <span className="text-xs text-sunset-muted">(tú)</span>}</td>
                <td className="px-5 py-4 text-sunset-muted">{u.email}</td>
                <td className="px-5 py-4"><span className={`text-xs px-2 py-1 rounded ${ROLE_COLORS[u.role]}`}>{ROLE_LABEL[u.role]}</span></td>
                <td className="px-5 py-4">{u.active !== false ? <span className="text-green-700">Activo</span> : <span className="text-sunset-muted">Inactivo</span>}</td>
                <td className="px-5 py-4"><div className="flex justify-end gap-2">
                  <button onClick={() => openEdit(u)} className="p-2 hover:text-sunset-accent" data-testid={`edit-user-${u.id}`}><Edit size={16} /></button>
                  {u.id !== me?.id && <button onClick={() => del(u.id)} className="p-2 hover:text-red-600" data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></button>}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Editar usuario" : "Nuevo usuario"}>
        <Field label="Nombre"><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></Field>
        {!editing && <Field label="Email"><Input type="email" data-testid="user-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>}
        <Field label={editing ? "Nueva contraseña (dejar vacío para no cambiar)" : "Contraseña"}>
          <Input type="password" data-testid="user-password" value={form.password} onChange={(e) => set("password", e.target.value)} />
        </Field>
        <Field label="Rol">
          <Select value={form.role} onChange={(e) => set("role", e.target.value)} data-testid="user-role">
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Select>
        </Field>
        {editing && <div className="py-2 mb-4"><Toggle checked={form.active} onChange={(v) => set("active", v)} label="Cuenta activa" /></div>}
        <Btn variant="accent" onClick={save} data-testid="save-user-btn">Guardar</Btn>
      </Modal>
    </div>
  );
}
